import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';

const POLL_INTERVAL = 3000;

export default function Logs() {
  const [lines, setLines] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [numLines, setNumLines] = useState(200);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const preRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    const fetchLogs = () => {
      api.getLiveLogs(numLines)
        .then(d => {
          setLines(d.lines || []);
          setTotalSize(d.total_size || 0);
        })
        .catch(() => {});
    };
    fetchLogs();
    const iv = setInterval(fetchLogs, POLL_INTERVAL);
    return () => clearInterval(iv);
  }, [numLines, paused]);

  useEffect(() => {
    if (autoScroll && preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  const handleScroll = () => {
    if (!preRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = preRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 40;
    setAutoScroll(atBottom);
  };

  const filtered = filter
    ? lines.filter(l => l.toLowerCase().includes(filter.toLowerCase()))
    : lines;

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const colorize = (line) => {
    if (line.includes('[ERR') || line.includes('ERRO') || line.includes('Traceback') || line.includes('Error'))
      return 'var(--accent-r)';
    if (line.includes('[!]') || line.includes('WARN') || line.includes('falhou'))
      return 'var(--accent-a)';
    if (line.includes('[REEL AD') || line.includes('[FEED AD') || line.includes('[+]'))
      return 'var(--accent-g)';
    if (line.includes('[*]') || line.includes('SESSAO'))
      return 'var(--accent)';
    if (line.includes('[POPUP]') || line.includes('[CTX]') || line.includes('[RECOVER]'))
      return 'var(--accent-p)';
    return '#a1a1aa';
  };

  return (
    <div style={s.page}>
      {/* Toolbar */}
      <div style={s.toolbar}>
        <div style={s.toolLeft}>
          <span style={s.title}>Live Logs</span>
          <span style={s.badge}>{formatSize(totalSize)}</span>
          <span style={{ ...s.badge, background: paused ? 'var(--accent-r)' : 'var(--accent-g)', color: '#fff' }}>
            {paused ? 'Pausado' : 'Ao vivo'}
          </span>
        </div>
        <div style={s.toolRight}>
          <input
            style={s.filter}
            placeholder="Filtrar..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <select style={s.select} value={numLines} onChange={e => setNumLines(Number(e.target.value))}>
            <option value={100}>100 linhas</option>
            <option value={200}>200 linhas</option>
            <option value={500}>500 linhas</option>
            <option value={1000}>1000 linhas</option>
          </select>
          <button style={{ ...s.btn, background: paused ? 'var(--accent-g)' : 'var(--accent-a)' }}
            onClick={() => setPaused(!paused)}>
            {paused ? 'Retomar' : 'Pausar'}
          </button>
        </div>
      </div>

      {/* Log viewer */}
      <div style={s.terminal} ref={preRef} onScroll={handleScroll}>
        {filtered.length > 0 ? filtered.map((line, i) => (
          <div key={i} style={{ ...s.line, color: colorize(line) }}>
            <span style={s.lineNum}>{i + 1}</span>
            {line.replace(/\n$/, '')}
          </div>
        )) : (
          <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>
            {paused ? 'Polling pausado' : 'Sem logs disponíveis'}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <span>{filtered.length} linhas{filter ? ` (filtrado de ${lines.length})` : ''}</span>
        <span>Auto-scroll: {autoScroll ? 'ON' : 'OFF (scroll para o final para reativar)'}</span>
        <span>Refresh: {POLL_INTERVAL / 1000}s</span>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex', flexDirection: 'column', height: 'calc(100vh - 49px)',
    background: 'var(--bg)', padding: 0,
  },
  toolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  toolLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  toolRight: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { fontSize: 13, fontWeight: 700, color: '#e4e4e7' },
  badge: {
    fontSize: 10, padding: '2px 8px', borderRadius: 4,
    background: 'var(--border)', color: '#a1a1aa', fontWeight: 500,
  },
  filter: {
    fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '1px solid #333',
    background: '#1a1a1a', color: '#e4e4e7', outline: 'none', width: 160,
  },
  select: {
    fontSize: 11, padding: '4px 8px', borderRadius: 4, border: '1px solid #333',
    background: '#1a1a1a', color: '#e4e4e7', outline: 'none',
  },
  btn: {
    fontSize: 11, padding: '4px 12px', borderRadius: 4, border: 'none',
    color: '#fff', fontWeight: 600, cursor: 'pointer',
  },
  terminal: {
    flex: 1, overflow: 'auto', padding: '8px 0', fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: 11, lineHeight: 1.6,
  },
  line: {
    padding: '0 16px', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    display: 'flex', gap: 12,
  },
  lineNum: {
    color: '#333', minWidth: 36, textAlign: 'right', userSelect: 'none', flexShrink: 0,
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', padding: '6px 16px',
    background: 'var(--surface)', borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)',
    flexShrink: 0,
  },
};
